<template>
  <div>
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <ErrorBox
      v-if="errorHeading || errorContent"
      :heading="errorHeading"
      :content="errorContent"
      :fixed="errorFixed"
      @closeError="
        errorHeading = '';
        errorContent = '';
      "
    ></ErrorBox>

    <div
      class="p-4 grid grid-cols-1 auto-rows-min sm:grid-cols-1 gap-4 custom-height"
    >
      <div v-if="loaded" class="p-8 bg-white shadow">
        <!-- Summary -->
        <h1 class="text-2xl mb-8">{{ $t("pages.usage.allowanceTitle") }}</h1>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <InfoItem
            :heading="$t('pages.usage.currentLicense')"
            :value="usage.license"
          ></InfoItem>
          <InfoItem
            :heading="$t('pages.usage.licenseExpiry')"
            :value="getPrettyDate(usage.licenseExpiry)"
          ></InfoItem>
          <InfoItem
            :heading="$t('pages.usage.hoursUsed')"
            :value="getHoursUsed()"
          ></InfoItem>
          <InfoItem
            :heading="$t('pages.usage.currentBalance')"
            :value="getHoursLeft()"
            :no-padding="true"
          ></InfoItem>
        </div>

        <h2 class="text-2xl mb-8">{{ $t("pages.usage.usageHistory") }}</h2>
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div class="w-96">
            <Select
              id="ors"
              v-model:value="selectedRange"
              :label="$t('pages.usage.dateRange')"
              :options="rangeOptions"
              @change="onRangeChange"
            />
          </div>

          <div class="flex-grow mb-6">
            <div v-if="selectedRange === 'custom'">
              <label class="block w-full mb-1">{{
                $t("pages.usage.dateRanges.custom")
              }}</label>
              <VueDatePicker
                v-model="date"
                range
                :multi-calendars="{ solo: true }"
                class="w-96"
                :format-locale="datePickerLocale"
                format="P HH:mm"
                @update:model-value="updateUsageHistory"
              />
            </div>
          </div>

          <ButtonOutlined
            v-if="usageHistory?.usageEvents?.length"
            type="button"
            :name="$t('pages.usage.exportAsCsv')"
            class="w-max px-6"
            @click="exportAsCSV()"
          ></ButtonOutlined>
        </div>

        <div
          v-if="usageHistoryLoading"
          class="loading-element flex items-center justify-center transition-all duration-1000 ease-in-out"
        >
          <div
            class="relative z-30 text-3xl text-gray-600 capitalize animate-pulse"
          >
            {{ $t("pages.usage.loadingUsageData") }}
          </div>
        </div>

        <div
          v-if="usageHistory?.usageEvents?.length && !usageHistoryLoading"
          class="max-h-max-content"
        >
          <div v-if="date" class="mb-4">
            {{
              getPrettyDateTime(date[0]) + " - " + getPrettyDateTime(date[1])
            }}
          </div>
          <table class="w-full text-sm text-left table-auto">
            <thead class="bg-nucleus-primaryBlue text-white sticky top-0">
              <tr>
                <th class="px-4 py-3 z-10">
                  {{ $t("pages.usage.table.columns.timestamp") }}
                </th>
                <th class="px-4 py-3 z-10">
                  {{ $t("pages.usage.table.columns.type") }}
                </th>
                <th class="px-4 py-3 z-10">
                  {{ $t("pages.usage.table.columns.duration") }}
                </th>
                <th class="px-4 py-3 z-10">
                  {{ $t("pages.usage.table.columns.credits") }}
                </th>
                <th class="px-4 py-3 z-10">
                  {{ $t("pages.usage.table.columns.balance") }}
                </th>
              </tr>
            </thead>
            <tbody class="nucleus-table-body">
              <template
                v-for="item in usageHistory.usageEvents"
                :key="item.eventId"
              >
                <tr
                  class="cursor-pointer relative"
                  :class="
                    expandedRows[item.eventId]
                      ? 'border-2 border-nucleus-primaryBlue border-b-0 active'
                      : 'border-2 border-transparent border-b-0'
                  "
                  @click="toggleRow(item)"
                >
                  <td class="px-4 py-3">
                    <div v-if="item.timestamp">
                      {{ getPrettyDateTime(item.timestamp) }}
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div v-if="item.eventType">
                      {{ formatEventType(item.eventType) }}
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div v-if="item.sessionDuration">
                      {{ formatDuration(item.sessionDuration) }}
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div v-if="item.credits">
                      {{ formatCreditsChange(item.eventType, item.credits) }}
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div v-if="item.credits">
                      {{ formatCredits(item.balance) }}
                    </div>
                  </td>
                </tr>
                <tr
                  v-show="expandedRows[item.eventId]"
                  class="bg-nucleus-listBgActive"
                  :class="
                    expandedRows[item.eventId]
                      ? 'border-2 border-nucleus-primaryBlue border-t-0'
                      : 'border-2 border-transparent border-t-0'
                  "
                >
                  <td colspan="5" class="px-4 py-4">
                    <div
                      v-if="item.session"
                      class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm"
                    >
                      <div>
                        <div class="font-semibold mb-1">
                          {{ $t("pages.usage.details.sessionDetails") }}
                        </div>
                        <div v-if="item.session.roomName">
                          {{ $t("pages.usage.details.room") }}
                          {{ item.session.roomName }}
                        </div>
                        <div v-if="item.session.department">
                          {{ $t("pages.usage.details.department") }}
                          {{ item.session.department }}
                        </div>
                        <div v-if="item.session.startedAt">
                          {{ $t("pages.usage.details.startedAt") }}
                          {{ getPrettyDateTime(item.session.startedAt) }}
                        </div>
                        <div v-if="item.session.endedAt">
                          {{ $t("pages.usage.details.endedAt") }}
                          {{ getPrettyDateTime(item.session.endedAt) }}
                        </div>
                        <div v-if="item.session.endedAt">
                          {{ $t("pages.usage.details.duration") }}
                          {{ formatDuration(item.sessionDuration) }}
                        </div>
                        <div v-if="item.credits">
                          {{ $t("pages.usage.details.creditsUsed") }}
                          {{ formatCredits(item.credits) }}
                        </div>
                      </div>
                      <div>
                        <div class="font-semibold mb-1">
                          {{ $t("pages.usage.details.userDetails") }}
                        </div>
                        <div v-if="item.session.createdBy">
                          {{ $t("pages.usage.details.createdBy") }}
                          {{ item.session.createdBy }}
                        </div>
                        <div v-if="item.session.startedBy">
                          {{ $t("pages.usage.details.startedBy") }}
                          {{ item.session.startedBy }}
                        </div>
                      </div>
                      <div
                        v-if="
                          item.session.joinedUsers &&
                          item.session.joinedUsers.length
                        "
                      >
                        <div class="font-semibold mb-1">
                          {{ $t("pages.usage.details.participants") }}
                        </div>
                        <div
                          v-for="user in item.session.joinedUsers"
                          :key="user"
                        >
                          {{ user }}
                        </div>
                      </div>
                    </div>
                    <div v-else>{{ $t("pages.usage.details.noData") }}</div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center text-gray-500">
          <div v-if="!date">
            {{ $t("pages.usage.selectDateRange") }}
          </div>
          <div v-if="date && !usageHistoryLoading">
            {{ $t("pages.usage.noData") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import InfoItem from "@/components/utils/InfoItem.vue";
import { getPrettyDate, getPrettyDateTime } from "@/utils/prettyDate";
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";
import { getLocalisedError } from "@/utils/apiUtils";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import ButtonOutlined from "@/components/utils/ButtonOutlined.vue";
import moment from "moment";
import { enGB, enUS, ja } from "date-fns/locale";

@Options({
  components: {
    InfoItem,
    LoadingScreen,
    ErrorBox,
    VueDatePicker,
    ButtonOutlined,
    Select,
  },
  data() {
    return {
      usage: {
        license: "",
        licenseExpiry: "",
        creditsTotal: 0,
        creditsUsed: 0,
        creditsExpired: 0,
      },
      usageHistory: {},
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      loadingMessage: this.$t("global.defaults.loading"),
      loaded: false,
      date: null,
      expandedRows: {},
      usageHistoryLoading: false,
      selectedRange: "",
      rangeOptions: [
        { id: "last7Days", name: this.$t("pages.usage.dateRanges.last7Days") },
        {
          id: "last30Days",
          name: this.$t("pages.usage.dateRanges.last30Days"),
        },
        { id: "lastWeek", name: this.$t("pages.usage.dateRanges.lastWeek") },
        { id: "lastMonth", name: this.$t("pages.usage.dateRanges.lastMonth") },
        { id: "custom", name: this.$t("pages.usage.dateRanges.custom") },
      ],
    };
  },
  computed: {
    datePickerLocale() {
      if (this.$store.getters.getLanguage === "ja") {
        return ja;
      }
      if (navigator.language === "en-US") {
        return enUS;
      }
      return enGB;
    },
  },
  mounted() {
    document.title =
      this.$t("global.defaults.usage") +
      " - " +
      this.$t("global.defaults.browserTitle");
    this.getUsage();
  },
  methods: {
    getPrettyDate(date: Date) {
      return getPrettyDate(date);
    },
    getPrettyDateTime(date: Date) {
      return getPrettyDateTime(date);
    },
    onRangeChange() {
      if (this.selectedRange === "custom") {
        this.date = null;
        this.updateUsageHistory();
        return;
      }

      let from: moment.Moment, to: moment.Moment;

      switch (this.selectedRange) {
        case "last7Days":
          from = moment().add(-7, "days");
          to = moment();
          break;

        case "last30Days":
          from = moment().add(-30, "days");
          to = moment();
          break;

        case "lastWeek":
          {
            from = moment()
              .add(-1, "week")
              .day(1)
              .hours(0)
              .minutes(0)
              .seconds(0)
              .milliseconds(0);
            to = moment().day(1).hours(0).minutes(0).seconds(0).milliseconds(0);
          }
          break;

        case "lastMonth":
          from = moment()
            .add(-1, "month")
            .date(1)
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0);
          to = moment().date(1).hours(0).minutes(0).seconds(0).milliseconds(0);
          break;

        default:
          return;
      }

      this.date = [from.toDate(), to.toDate()];
      this.updateUsageHistory();
    },
    getUsage() {
      this.$store
        .dispatch("getTenancyUsage")
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.usage = response.data;
            this.loaded = true;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          console.log(error, typeof error);
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    updateUsageHistory() {
      this.usageHistory = {};
      this.expandedRows = {};
      if (this.date) {
        this.getTenancyUsageHistory();
      }
    },
    getTenancyUsageHistory() {
      this.usageHistoryLoading = true;
      const params = {
        from: this.date[0].toISOString(),
        to: this.date[1].toISOString(),
      };
      // console.log(params);
      this.$store
        .dispatch("getTenancyUsageHistory", params)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.usageHistory = response.data;
            this.usageHistoryLoading = false;
          }
        })
        .catch((error: any) => {
          console.log(error, typeof error);
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
          this.usageHistoryLoading = false;
        });
    },
    getHoursLeft() {
      let remaining =
        this.usage.creditsTotal -
        this.usage.creditsUsed -
        this.usage.creditsExpired;
      let remainingText;
      if (remaining === 0) {
        remainingText = this.$t("pages.usage.0Hours");
      } else if (remaining > -1 && remaining < 0) {
        remainingText = this.$t("pages.usage.0HoursDebt");
      } else if (remaining < 0) {
        remainingText = this.$t("pages.usage.0HoursDebtAmount", {
          remaining: Math.floor(Math.abs(remaining)),
        });
      } else if (remaining > 0 && remaining < 1) {
        remainingText = this.$t("pages.usage.lessThan1Remaining");
      } else {
        remainingText = this.$t("pages.usage.remaining", {
          remaining: Math.floor(remaining),
        });
      }
      return remainingText;
    },
    getHoursUsed() {
      let usedText;
      if (this.usage.creditsUsed > 0 && this.usage.creditsUsed < 1) {
        usedText = this.$t("pages.usage.lessThan1Used", {
          remaining: this.usage.creditsTotal,
        });
      } else {
        usedText = this.$t("pages.usage.used", {
          used: Math.floor(this.usage.creditsUsed),
          total: this.usage.creditsTotal,
        });
      }
      return usedText;
    },
    getCsvDate(dateStr: string) {
      const date = new Date(dateStr);
      return date.toISOString().replace("T", " ").substring(0, 19);
    },

    formatDuration(hours: number): string {
      const duration = moment.duration(hours, "hours");
      return `${duration.hours().toString().padStart(2, "0")}:${duration
        .minutes()
        .toString()
        .padStart(2, "0")}:${duration.seconds().toString().padStart(2, "0")}`;
    },
    formatCredits(credits: number): string {
      return credits ? credits.toFixed(2) : "";
    },
    formatCreditsChange(type: string, credits: number): string {
      if (credits === null || credits === undefined) return "";

      return type === "DEBIT"
        ? "-" + credits.toFixed(2)
        : "+" + credits.toFixed(2);
    },
    formatEventType(eventType: string) {
      if (eventType === "DEBIT")
        return this.$t("pages.usage.table.values.debit");
      if (eventType === "CREDIT")
        return this.$t("pages.usage.table.values.credit");
      return "";
    },
    toggleRow(item: any) {
      if (!item.session) return;
      const id = item.eventId;
      this.expandedRows = {
        ...this.expandedRows,
        [id]: !this.expandedRows[id],
      };
    },
    exportAsCSV() {
      if (
        !this.usageHistory ||
        !this.usageHistory.usageEvents ||
        this.usageHistory.usageEvents.length === 0
      ) {
        return;
      }
      const header = [
        this.$t("pages.usage.csv.timestamp"),
        this.$t("pages.usage.csv.type"),
        this.$t("pages.usage.csv.duration"),
        this.$t("pages.usage.csv.credits"),
        this.$t("pages.usage.csv.balance"),
        this.$t("pages.usage.csv.roomName"),
        this.$t("pages.usage.csv.department"),
      ];
      const rows = this.usageHistory.usageEvents.map((item: any) => [
        item.timestamp ? this.getCsvDate(item.timestamp) : "",
        item.eventType || "",
        item.sessionDuration ? this.formatDuration(item.sessionDuration) : "",
        item.credits != null
          ? this.formatCredits(
              item.eventType === "DEBIT" ? item.credits * -1 : item.credits
            )
          : "",
        item.balance != null ? this.formatCredits(item.balance) : "",
        item.session?.roomName || "",
        item.session?.department || "",
      ]);
      const csvContent = [header, ...rows]
        .map((row) => row.join(","))
        .join("\n");
      // Add BOM for Excel (see https://support.microsoft.com/en-gb/office/opening-csv-utf-8-files-correctly-in-excel-8a935af5-3416-4edd-ba7e-3dfd2bc4a032)
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      let filename = "Usage.csv";
      if (this.date[0] && this.date[1]) {
        const fromDate = new Date(
          this.date[0].getTime() - this.date[0].getTimezoneOffset() * 60 * 1000
        )
          .toISOString()
          .split("T")[0];
        const toDate = new Date(
          this.date[1].getTime() - this.date[1].getTimezoneOffset() * 60 * 1000
        )
          .toISOString()
          .split("T")[0];
        filename = `Usage_${fromDate}_${toDate}.csv`;
      }
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
})
export default class Usage extends Vue {}
</script>

<style lang="scss" scoped>
.custom-height {
  height: calc(100vh - 144px);
}

@layer utilities {
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

.nucleus-table-body {
  tr {
    @apply bg-nucleus-primaryBlue bg-opacity-5;
  }
  tr:nth-child(2n + 1) {
    &::after {
      content: "";
      @apply absolute top-1/2 -translate-y-full right-4 w-2 h-2 border-nucleus-primaryBlue border-r-2 border-b-2 transform rotate-45 transition-transform origin-center;
    }
    &.active::after {
      --tw-rotate: 225deg;
    }
  }
  tr:nth-child(4n + 3),
  tr:nth-child(4n + 4) {
    @apply bg-opacity-10;
  }
}
</style>
